// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/contador/PendenciasSemana.tsx
//  Pendências da carteira (o mesmo conteúdo do e-mail de segunda): risco no Simples,
//  cliente sem documento, numeração faltante, inconsistência fiscal e IBS/CBS. Cada
//  linha leva ao cliente; a chave liga ou desliga o e-mail semanal deste usuário.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, ClipboardList, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { contadorService, rotuloCompetencia, type Pendencia } from './contadorService';

/** Linhas mostradas antes de "ver todas". */
const VISIVEIS = 6;

const ROTULO_TIPO: Record<Pendencia['tipo'], string> = {
  simples: 'Simples', envio: 'Envio', numeracao: 'Numeração', fiscal: 'Fiscal', reforma: 'IBS/CBS',
};

export function PendenciasSemana({ competenciaDaCarteira }: { competenciaDaCarteira: number }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [todas, setTodas] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['contador', 'resumo'],
    queryFn: () => contadorService.resumo(),
    staleTime: 10 * 60_000,
  });
  const preferencia = useMutation({
    mutationFn: (receber: boolean) => contadorService.preferencias(receber),
    onSuccess: (_, receber) => {
      qc.setQueryData(['contador', 'resumo'], { ...data!, receberPorEmail: receber });
      toast.success(receber ? 'Você vai receber o resumo toda segunda.' : 'O resumo semanal por e-mail foi desligado.');
    },
    onError: () => toast.error('Não foi possível salvar a preferência.'),
  });

  if (isLoading) return <Skeleton className="h-32" />;
  if (!data || data.clientes === 0) return null;

  const lista = todas ? data.pendencias : data.pendencias.slice(0, VISIVEIS);
  const altas = data.pendencias.filter((p) => p.gravidade === 'alta').length;

  return (
    <Card className={altas > 0 ? 'border-amber-500' : undefined}>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-start gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />Pendências da semana
              {data.pendencias.length > 0 && <Badge variant="secondary">{data.pendencias.length}</Badge>}
            </CardTitle>
            <CardDescription>
              Da carteira inteira; numeração, fiscal e IBS/CBS olham {rotuloCompetencia(data.mesFechado).toLowerCase()}.
            </CardDescription>
          </div>
          <div className="sm:ml-auto flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <Label htmlFor="resumo-email" className="text-sm font-normal">Receber por e-mail às segundas</Label>
            <Switch id="resumo-email" checked={data.receberPorEmail} disabled={preferencia.isPending}
              onCheckedChange={(v) => preferencia.mutate(v)} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-1">
        {data.pendencias.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-green-600" />Nenhuma pendência na carteira.
          </p>
        ) : (
          <>
            {lista.map((p, i) => (
              <button key={i} type="button"
                onClick={() => navigate(`/contador/clientes/${p.contribuinteId}?competencia=${p.tipo === 'simples' || p.tipo === 'envio' ? competenciaDaCarteira : data.mesFechado}`)}
                className="w-full flex items-start gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted">
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${p.gravidade === 'alta' ? 'bg-red-600' : 'bg-amber-500'}`} />
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{p.cliente}</span>
                  <span className="text-muted-foreground"> — {p.mensagem}</span>
                </span>
                <Badge variant="outline" className="shrink-0 hidden sm:inline-flex">{ROTULO_TIPO[p.tipo]}</Badge>
              </button>
            ))}
            {data.pendencias.length > VISIVEIS && (
              <Button variant="ghost" size="sm" onClick={() => setTodas(!todas)}>
                <ChevronDown className={`mr-1 h-4 w-4 transition-transform ${todas ? 'rotate-180' : ''}`} />
                {todas ? 'Mostrar menos' : `Ver todas (${data.pendencias.length})`}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
